/* Lustfabrik Atelier — theme behaviour
   Drawers, cart, sleeve drawings, model lineup, fit builder, product configurator, reveals. */
(() => {
  const theme = window.theme || { routes: { root: '/', cartAdd: '/cart/add', cartChange: '/cart/change', cart: '/cart' }, moneyFormat: '€{{amount_with_comma_separator}}', strings: {} };
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const NS = 'http://www.w3.org/2000/svg';

  // ─── Money ────────────────────────────────────────────────
  function formatMoney(cents, format = theme.moneyFormat) {
    const fmt = (decimals, thousands = ',', decimal = '.') => {
      const [int, frac] = (cents / 100).toFixed(decimals).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
    };
    const amounts = {
      amount: () => fmt(2),
      amount_no_decimals: () => fmt(0),
      amount_with_comma_separator: () => fmt(2, '.', ','),
      amount_no_decimals_with_comma_separator: () => fmt(0, '.', ','),
      amount_with_apostrophe_separator: () => fmt(2, "'", '.'),
      amount_with_space_separator: () => fmt(2, ' ', ','),
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => (amounts[key] || amounts.amount)());
  }

  // Sizes are made (and configured) in inches; customers see centimetres.
  // inches('5.25"') → 5.25, cm(5.5) → '14.0', fmt(5.25) → '5.25'
  const inches = (label) => parseFloat(String(label).replace(/[^\d.]/g, ''));
  const cm = (inch) => (Math.round(inch * 25.4) / 10).toFixed(1);
  const dec = (text) => String(text).replace(/(\d)\.(\d)/g, `$1${theme.decimal || '.'}$2`); // 13.3 → 13,3 in German
  const tr = (template, values) => String(template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => values[k] ?? '');
  const fmt = (n) => (Number.isInteger(n) ? String(n) : String(+n.toFixed(3)));
  const fmtW = (n) => (n === 1 ? '1.0' : fmt(n));
  // Both units, centimetres first: both(5.5) → '14.0 cm (5.5 in)'
  const both = (inch) => dec(`${cm(inch)} cm (${fmt(+inch.toFixed(3))} in)`);
  // A choice input's size in inches: data-in when the label is metric, else parsed from its value
  const inchOf = (input) => (input.dataset.in ? parseFloat(input.dataset.in) : inches(input.value));

  // ─── Drawers (menu, search, cart) ─────────────────────────
  function openDrawer(dialog) {
    if (!dialog || dialog.open) return;
    dialog.showModal();
    document.body.classList.add('is-locked');
    requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-open')));
    const focusTarget = $('input[type="search"]', dialog);
    if (focusTarget) setTimeout(() => focusTarget.focus(), 50);
  }
  function closeDrawer(dialog) {
    if (!dialog || !dialog.open) return;
    dialog.classList.remove('is-open');
    const panel = $('.drawer__panel', dialog);
    const done = () => { dialog.close(); document.body.classList.remove('is-locked'); };
    if (reduceMotion || !panel) return done();
    panel.addEventListener('transitionend', done, { once: true });
    setTimeout(() => dialog.open && done(), 600);
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.open);
      if (dialog) {
        e.preventDefault();
        openDrawer(dialog);
      }
      return;
    }
    const closer = e.target.closest('[data-close]');
    if (closer) closeDrawer(closer.closest('dialog'));
  });
  $$('dialog.drawer').forEach((dialog) => {
    dialog.addEventListener('cancel', (e) => { e.preventDefault(); closeDrawer(dialog); });
  });

  // ─── Cart ─────────────────────────────────────────────────
  const cartDrawer = () => document.getElementById('cart-drawer');

  function setCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count > 0 ? count : '';
      if (!reduceMotion) { el.classList.remove('is-bumped'); void el.offsetWidth; el.classList.add('is-bumped'); }
    });
  }

  async function refreshCart() {
    const drawer = cartDrawer();
    if (!drawer) return;
    const res = await fetch(`${theme.routes.root}?sections=cart-drawer`);
    const data = await res.json();
    const doc = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
    const fresh = $('[data-cart-root]', doc);
    const count = $('[data-cart-count-source]', doc);
    if (fresh) $('[data-cart-root]', drawer).innerHTML = fresh.innerHTML;
    if (count) setCount(Number(count.textContent));
  }

  async function cartPost(url, body) {
    const res = await fetch(`${url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok || json.status) throw new Error(json.description || json.message || theme.strings.addError);
    return json;
  }

  // Quantity steppers (product form + cart lines)
  document.addEventListener('click', (e) => {
    const step = e.target.closest('[data-qty-step]');
    if (!step) return;
    const input = $('input', step.closest('.qty'));
    const min = Number(input.min || 0);
    input.value = Math.max(min, Number(input.value || 0) + Number(step.dataset.qtyStep));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  // Cart line changes — in the drawer they re-render, on the cart page the page reloads
  async function changeLines(line, updates) {
    line.classList.add('is-busy');
    try {
      await cartPost(`${theme.routes.cart}/update`, { updates });
      if (line.closest('[data-cart-page]')) return window.location.reload();
      await refreshCart();
    } catch (err) {
      line.classList.remove('is-busy');
      console.error(err);
    }
  }
  // Removing a sleeve also removes its add-ons (they share the sleeve's hidden _group)
  function linesToRemove(line) {
    const keys = [line.dataset.lineKey];
    const group = line.dataset.group;
    if (group && !line.classList.contains('cart-line--addon')) {
      $$(`.cart-line--addon[data-group="${CSS.escape(group)}"]`, line.parentElement).forEach((l) => keys.push(l.dataset.lineKey));
    }
    return keys;
  }
  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-line-remove]');
    if (!remove) return;
    const line = remove.closest('[data-line-key]');
    const updates = {};
    linesToRemove(line).forEach((k) => (updates[k] = 0));
    changeLines(line, updates);
  });
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-line-qty]');
    if (!input) return;
    const line = input.closest('[data-line-key]');
    const qty = Math.max(0, Number(input.value) || 0);
    const updates = {};
    if (qty === 0) linesToRemove(line).forEach((k) => (updates[k] = 0));
    else updates[line.dataset.lineKey] = qty;
    changeLines(line, updates);
  });

  // ─── Sleeve cross-section, drawn to scale ─────────────────
  const S = 63.5;        // px per inch (25 px per cm); geometry is in inches, the ruler in cm
  const PX_CM = S / 2.54;
  const X0 = 128;        // where the open end sits (room for the width label)
  const CY = 124;        // centre line
  const MAX_R = 1.3 * S; // room for the widest sleeve

  function el(name, attrs, parent) {
    const node = document.createElementNS(NS, name);
    for (const k in attrs) node.setAttribute(k, attrs[k]);
    if (parent) parent.appendChild(node);
    return node;
  }

  // rulerCm: ruler length in centimetres (ticks every 5 mm, numbers every 2 cm)
  function buildSleeve(svg, rulerCm = 24) {
    rulerCm = Math.ceil(rulerCm / 2) * 2;
    svg.innerHTML = '';
    const rulerY = CY + MAX_R + 20;
    svg.setAttribute('viewBox', `0 0 ${X0 + rulerCm * PX_CM + 28} ${rulerY + 36}`);
    const pour = el('g', { class: 'pour' }, svg);
    const parts = {
      body: el('path', { class: 'body' }, pour),
      hole: el('path', { class: 'hole' }, pour),
      lenLine: el('path', { class: 'dim' }, svg),
      lenText: el('text', { class: 'dim-label', 'text-anchor': 'middle' }, svg),
      widLine: el('path', { class: 'dim' }, svg),
      widText: el('text', { class: 'dim-label', 'text-anchor': 'end' }, svg),
    };
    let d = `M${X0} ${rulerY}H${X0 + rulerCm * PX_CM}`;
    for (let h = 0; h <= rulerCm * 2; h++) {
      const x = X0 + (h / 2) * PX_CM;
      d += `M${x} ${rulerY}v${h % 4 === 0 ? 10 : h % 2 === 0 ? 7 : 4}`;
      if (h % 4 === 0) el('text', { class: 'tick-label', x, y: rulerY + 28, 'text-anchor': 'middle' }, svg).textContent = h / 2;
    }
    el('path', { class: 'dim', d }, svg);
    el('text', { class: 'tick-label', x: X0 - 14, y: rulerY + 28, 'text-anchor': 'end' }, svg).textContent = 'cm';
    return parts;
  }

  // g = { OL, OD, L, W, lenLabel?, widLabel? } in inches (labels are drawn in cm)
  function drawSleeve(parts, g) {
    const r = (g.OD * S) / 2;
    const rs = r * 0.86;
    const xe = X0 + g.OL * S;
    const head = Math.max(1.7, g.OD / 2 + 0.6) * S;
    const xh = xe - head;
    const flare = 0.5 * S;
    parts.body.setAttribute('d', [
      `M${X0} ${CY - rs}H${xh}`,
      `C${xh + flare * 0.35} ${CY - rs} ${xh + flare * 0.5} ${CY - r} ${xh + flare} ${CY - r}`,
      `H${xe - r}A${r} ${r} 0 0 1 ${xe - r} ${CY + r}H${xh + flare}`,
      `C${xh + flare * 0.5} ${CY + r} ${xh + flare * 0.35} ${CY + rs} ${xh} ${CY + rs}`,
      `H${X0}Z`,
    ].join(''));

    const L = Math.min(g.L, g.OL - 0.35);
    const rw = (Math.min(g.W, g.OD * 0.8) * S) / 2;
    const xi = X0 + L * S;
    parts.hole.setAttribute('d', `M${X0 - 1} ${CY - rw}H${xi - rw}A${rw} ${rw} 0 0 1 ${xi - rw} ${CY + rw}H${X0 - 1}Z`);

    const yl = CY - r - 16;
    parts.lenLine.setAttribute('d', `M${X0} ${yl}H${xi}M${X0} ${yl - 6}v12M${xi} ${yl - 6}v12`);
    parts.lenText.setAttribute('x', (X0 + xi) / 2);
    parts.lenText.setAttribute('y', yl - 10);
    parts.lenText.textContent = g.lenLabel || tr(theme.strings.insideLabel || '{{ size }} inside', { size: both(Math.round(g.L * 4) / 4) });

    const xw = X0 - 16;
    parts.widLine.setAttribute('d', `M${xw} ${CY - rw}V${CY + rw}M${xw - 6} ${CY - rw}h12M${xw - 6} ${CY + rw}h12`);
    parts.widText.setAttribute('x', xw - 10);
    // Width label on two lines, cm above inches, so it fits left of the drawing
    parts.widText.setAttribute('y', CY - 4);
    if (g.widLabel) {
      // e.g. 'choose a width' → 'choose' / 'a width', same two-line layout as the measurement
      parts.widText.textContent = '';
      const cut = g.widLabel.indexOf(' ');
      const lines = cut > 0 ? [g.widLabel.slice(0, cut), g.widLabel.slice(cut + 1)] : [g.widLabel];
      lines.forEach((line, i) => { el('tspan', { x: xw - 10, dy: i ? '1.2em' : 0 }, parts.widText).textContent = line; });
    } else {
      parts.widText.textContent = '';
      el('tspan', { x: xw - 10 }, parts.widText).textContent = dec(`${cm(g.W)} cm`);
      el('tspan', { x: xw - 10, dy: '1.2em', class: 'tick-label' }, parts.widText).textContent = dec(`(${fmtW(+g.W.toFixed(3))} in)`);
    }
  }

  // Tween geometry so a change reads as the mould resizing, not a jump
  function animator(parts) {
    let cur = null;
    let raf = 0;
    return (g) => {
      if (!cur || reduceMotion) { cur = { ...g }; drawSleeve(parts, g); return; }
      const from = { ...cur };
      const start = performance.now();
      cancelAnimationFrame(raf);
      const step = (now) => {
        const t = Math.min(1, (now - start) / 420);
        const e = 1 - Math.pow(1 - t, 3);
        const mix = (k) => from[k] + (g[k] - from[k]) * e;
        cur = { OL: mix('OL'), OD: mix('OD'), L: mix('L'), W: mix('W') };
        drawSleeve(parts, { ...cur, lenLabel: t < 1 ? null : g.lenLabel, widLabel: t < 1 ? null : g.widLabel });
        if (t < 1) raf = requestAnimationFrame(step);
        else cur = { ...g };
      };
      raf = requestAnimationFrame(step);
    };
  }

  // 'The Dirty Secret (7.5" x 2.36")' → { OL: 7.5, OD: 2.36 } in inches; imperfects also carry '- 5.75" x 1.125"'.
  // Titles written in cm, like 'The Dirty Secret (19 x 6 cm)', are converted to inches for the drawing.
  function parseTitle(title) {
    const toIn = title.includes('cm') ? 1 / 2.54 : 1;
    const outer = /\(([\d.,]+)\s*"?\s*[x×]\s*([\d.,]+)\s*(?:"|cm)?\)/.exec(title);
    const inner = /-\s*([\d.,]+)\s*"?\s*[x×]\s*([\d.,]+)\s*(?:"|cm)?\s*$/.exec(title);
    const num = (s) => parseFloat(s.replace(',', '.')) * toIn;
    return {
      OL: outer ? num(outer[1]) : null,
      OD: outer ? num(outer[2]) : null,
      L: inner ? num(inner[1]) : null,
      W: inner ? num(inner[2]) : null,
    };
  }

  // Data from sleeve-data.liquid → model with length/width/shade choices
  function toModel(p) {
    const find = (re) => (p.options || []).find((o) => re.test(o.name));
    const len = find(/length/i);
    const wid = find(/width/i);
    const shade = (p.options || []).find((o) => o.swatch);
    const dims = parseTitle(p.title);
    if (!len || !wid || !dims.OL) return null;
    return {
      ...p,
      ...dims,
      lengths: len.choices.map((c) => c.label),
      widths: wid.choices.map((c) => c.label),
      shades: shade ? shade.choices : [],
      lengthName: len.name,
      widthName: wid.name,
      shadeName: shade ? shade.name : null,
    };
  }

  // Hero (and any static) drawings — their sizes are set in cm in the theme editor
  $$('svg[data-sleeve][data-outer-length]').forEach((svg) => {
    const parts = buildSleeve(svg, 24);
    parts.body.style.fill = svg.dataset.color;
    const inch = (v) => Math.round((+v / 2.54) * 20) / 20; // nearest 0.05 in, so 2.5 cm reads as 1 in
    drawSleeve(parts, {
      OL: inch(svg.dataset.outerLength), OD: inch(svg.dataset.outerWidth),
      L: inch(svg.dataset.length), W: inch(svg.dataset.width),
    });
  });

  // ─── Model lineup ─────────────────────────────────────────
  const lineup = $('[data-lineup]');
  if (lineup) {
    const raw = JSON.parse($('[data-lineup-data]').textContent);
    // Shown in the collection's own order (set in the Shopify admin), with only sleeves that have a size in their title
    const limit = Number(lineup.dataset.limit) || 8;
    const models = raw.map((p) => ({ ...p, ...parseTitle(p.title) })).filter((m) => m.OL).slice(0, limit);
    const scaleMax = Number(lineup.dataset.scaleMax) || 24; // cm
    // Several sizes of one model share a name; tell them apart by outer length
    const shared = models.filter((m) => models.filter((o) => o.name === m.name).length > 1);
    shared.forEach((m) => { m.name = `${m.name} ${Math.round(m.OL * 2.54)} cm`; });
    if (models.length) {
      lineup.innerHTML = models.map((m, i) => {
        const lengthCm = +cm(m.OL);
        // The bar is the sleeve's outer length; its height follows the sleeve's thickness (4 px per cm)
        return `<a class="row" href="${m.url}">
          ${m.image ? `<img class="row__img" src="${m.image}" alt="" loading="lazy" width="72" height="72">` : '<span class="row__img"></span>'}
          <span class="row__name">${m.name}<span class="row__meta">${dec(`${cm(m.OL)} × ${cm(m.OD)} cm (${fmt(+m.OL.toFixed(2))} × ${fmt(+m.OD.toFixed(2))} in)`)}</span></span>
          <span class="row__bar" style="--min:0%;--max:${(Math.min(lengthCm, scaleMax) / scaleMax) * 100}%;--bar-h:${Math.round(+cm(m.OD) * 4)}px;--delay:${i * 70}ms"
            role="img" aria-label="Outer length ${cm(m.OL)} centimetres, diameter ${cm(m.OD)} centimetres"><span class="bar-range"></span></span>
          <span class="row__price">${m.priceText}<span class="arrow" aria-hidden="true">→</span></span>
        </a>`;
      }).join('');
      let ticks = '';
      for (let n = 0; n <= scaleMax; n += 2) ticks += `<span style="left:${(n / scaleMax) * 100}%">${n}${n === scaleMax ? ' cm' : ''}</span>`;
      const scale = $('[data-lineup-scale]');
      if (scale) scale.innerHTML = `<span></span><span></span><div class="scale__ticks">${ticks}</div>`;
    }
  }

  // ─── Fit builder ──────────────────────────────────────────
  const fit = $('[data-fit-builder]');
  const fitData = fit && $('[data-fit-data]', fit);
  if (fit && fitData) {
    const models = JSON.parse(fitData.textContent).map(toModel).filter(Boolean)
      .sort((a, b) => inches(a.lengths.at(-1)) - inches(b.lengths.at(-1)) || a.OL - b.OL);
    if (models.length) initFit(models);
  }

  function initFit(models) {
    const fModel = $('[data-fit-model]', fit);
    const fLength = $('[data-fit-length]', fit);
    const fWidth = $('[data-fit-width]', fit);
    const fShade = $('[data-fit-shade]', fit);
    const svg = $('[data-fit-svg]', fit);
    const parts = buildSleeve(svg, 24);
    const tween = animator(parts);
    const preferred = models.find((m) => /dirty-secret/.test(m.handle)) || models[Math.floor(models.length / 2)];
    const state = { model: preferred, li: 0, W: null, shade: null };

    fModel.innerHTML = models.map((m) => `<option value="${m.handle}">${m.name}: ${dec(`${cm(m.OL)} × ${cm(m.OD)} cm (${fmt(+m.OL.toFixed(2))} × ${fmt(+m.OD.toFixed(2))} in)`)}</option>`).join('');

    function renderChoices() {
      const m = state.model;
      fLength.max = m.lengths.length - 1;
      state.li = Math.min(state.li || Math.floor(m.lengths.length / 2), m.lengths.length - 1);
      if (!m.widths.includes(state.W)) state.W = m.widths[1] || m.widths[0];
      fWidth.innerHTML = m.widths.map((w) => `<label><input type="radio" name="fit-width" value='${w}'${w === state.W ? ' checked' : ''}><span class="pill--dual">${dec(`${cm(inches(w))} cm`)}<small>${dec(`${fmt(inches(w))} in`)}</small></span></label>`).join('');
      if (!m.shades.some((s) => s.label === state.shade)) state.shade = m.shades[0] && m.shades[0].label;
      fShade.innerHTML = m.shades.map((s) => `<label><input type="radio" name="fit-shade" value="${s.label}"${s.label === state.shade ? ' checked' : ''}><i style="--c:${s.hex}"></i>${s.label}</label>`).join('');
    }

    function setTicket(key, text) {
      const node = $(`[data-ticket="${key}"]`, fit);
      if (!node || node.textContent === text) return;
      node.textContent = text;
      if (!reduceMotion) { node.classList.remove('flash'); void node.offsetWidth; node.classList.add('flash'); }
    }

    function update() {
      const m = state.model;
      const lenLabel = m.lengths[state.li];
      const L = inches(lenLabel);
      const W = inches(state.W);
      const shade = m.shades.find((s) => s.label === state.shade);
      fModel.value = m.handle;
      fLength.value = state.li;
      $('[data-fit-length-out]', fit).textContent = both(L);
      $('[data-fit-length-note]', fit).textContent = tr(theme.strings.lengthNote, { min: both(inches(m.lengths[0])), max: both(inches(m.lengths.at(-1))) });
      if (shade) parts.body.style.fill = shade.hex;
      tween({ OL: m.OL, OD: m.OD, L, W });
      svg.setAttribute('aria-label', `${theme.strings.drawingLabel || ''}: ${m.name}, ${both(L)}, ${both(W)}, ${state.shade}`);
      setTicket('model', m.name);
      setTicket('length', both(L));
      setTicket('width', both(W));
      setTicket('shade', state.shade || '–');
      setTicket('price', m.priceText);
      const params = new URLSearchParams({ length: fmt(L), width: fmtW(W), shade: state.shade || '' });
      $('[data-fit-link]', fit).href = `${m.url}?${params}`;
    }

    fModel.addEventListener('change', () => {
      const prevL = inches(state.model.lengths[state.li]);
      state.model = models.find((m) => m.handle === fModel.value) || state.model;
      // keep the same length if the new model offers it, else the closest
      const ls = state.model.lengths.map(inches);
      state.li = ls.reduce((best, l, i) => (Math.abs(l - prevL) < Math.abs(ls[best] - prevL) ? i : best), 0);
      renderChoices();
      update();
    });
    fLength.addEventListener('input', () => { state.li = Number(fLength.value); update(); });
    fWidth.addEventListener('change', (e) => { state.W = e.target.value; update(); });
    fShade.addEventListener('change', (e) => { state.shade = e.target.value; update(); });

    renderChoices();
    update();
  }

  // ─── Product page ─────────────────────────────────────────
  $$('[data-product]').forEach(initProduct);

  function initProduct(section) {
    const form = $('form[action*="/cart/add"]', section);
    if (!form) return;
    const variants = JSON.parse($('[data-variants]', section).textContent);
    const idInput = $('[data-variant-id]', form);
    const priceEl = $('[data-price]', section);
    const compareEl = $('[data-compare-price]', section);
    const noteEl = $('[data-price-note]', section);
    const addBtn = $('[data-add-button]', form);
    const errorEl = $('[data-form-error]', form);
    const totalEl = $('[data-total]', section);
    const workOrder = $('[data-work-order]', section);
    const stickyPrice = $('[data-sticky-price]', section);
    const productTitle = section.dataset.productTitle;
    let variant = variants.find((v) => String(v.id) === idInput.value) || variants[0];

    // Drawing
    const drawingSvg = $('[data-product-drawing]', section);
    let draw = null;
    let dims = null;
    if (drawingSvg) {
      dims = parseTitle(drawingSvg.dataset.title);
      if (dims.OL) {
        const parts = buildSleeve(drawingSvg, dims.OL * 2.54);
        parts.body.style.fill = 'var(--shade-a)';
        const tween = animator(parts);
        draw = (opts) => {
          if (opts.hex) parts.body.style.fill = opts.hex;
          tween(opts);
        };
      }
    }

    // Configured options (length / width / shade as line-item properties)
    const configSets = $$('[data-config-option]', form);
    const lengthSet = configSets.find((f) => /length/i.test(f.dataset.configOption));
    const widthSet = configSets.find((f) => /width/i.test(f.dataset.configOption));
    const shadeSet = configSets.find((f) => $('.swatches', f));
    const checked = (set) => (set ? $('input:checked', set) : null);
    const middle = (set) => { const all = $$('input', set); return all[Math.floor(all.length / 2)]; };

    // Prefill from the fit builder: ?length=5.5&width=1.0&shade=A
    const params = new URLSearchParams(window.location.search);
    const prefill = (set, value, numeric) => {
      if (!set || !value) return;
      const match = $$('input', set).find((i) => (numeric ? inchOf(i) === inches(value) : i.value === value));
      if (match) match.checked = true;
    };
    prefill(lengthSet, params.get('length'), true);
    prefill(widthSet, params.get('width'), true);
    prefill(shadeSet, params.get('shade'), false);

    // Add-ons
    const addonBox = $('[data-addons]', form);
    const addonRows = addonBox ? $$('.addon', addonBox) : [];
    function selectedAddons() {
      return addonRows.filter((row) => $('[data-addon-check]', row).checked).map((row) => {
        const check = $('[data-addon-check]', row);
        const select = $('[data-addon-variant]', row);
        const source = select ? select.selectedOptions[0] : check;
        const properties = { 'For sleeve': productTitle };
        $$('[data-addon-property]', row).forEach((f) => (properties[f.dataset.addonProperty] = f.value));
        const extra = select ? `, ${source.textContent.split(' (+')[0].trim()}` : '';
        return { id: Number(select ? source.value : check.dataset.variantId), price: Number(source.dataset.price), title: check.dataset.title + extra, properties };
      });
    }

    function flashDd(dd, text) {
      if (dd.textContent === text) return;
      dd.textContent = text;
      if (!reduceMotion) { dd.classList.remove('flash'); void dd.offsetWidth; dd.classList.add('flash'); }
    }

    // Shorten Sleeve: only offer amounts that fit the chosen internal length.
    // Rule: longest internal length offered for this sleeve − chosen length, at most 2 in.
    const shortenRow = addonRows.find((row) => row.dataset.addon === 'shorten-sleeve');
    const shortenCheck = shortenRow ? $('[data-addon-check]', shortenRow) : null;
    const shortenSelect = shortenRow ? $('[data-addon-variant]', shortenRow) : null;
    let shortenNote = null;
    let shortenResult = null;
    if (shortenRow && shortenSelect && lengthSet) {
      shortenNote = document.createElement('p');
      shortenNote.className = 'addon__note';
      $('.addon__main', shortenRow).after(shortenNote);
      shortenResult = document.createElement('p');
      shortenResult.className = 'addon__result';
      shortenSelect.closest('.addon__field').after(shortenResult);
    }
    function syncShorten() {
      if (!shortenNote) return;
      const chosen = checked(lengthSet);
      const longest = Math.max(...$$('input', lengthSet).map(inchOf));
      const room = chosen ? Math.min(2, longest - inchOf(chosen)) : 0;
      const options = [...shortenSelect.options];
      options.forEach((o) => {
        const fits = Number(o.dataset.in) <= room + 0.001;
        o.disabled = !fits;
        o.hidden = !fits;
      });
      const usable = options.filter((o) => !o.disabled);
      const locked = !chosen || usable.length === 0;
      if (locked && shortenCheck.checked) {
        shortenCheck.checked = false;
        const fields = $('.addon__fields', shortenRow);
        if (fields) fields.hidden = true;
      }
      shortenCheck.disabled = locked;
      shortenRow.classList.toggle('is-locked', locked);
      shortenNote.hidden = !locked;
      shortenNote.textContent = !chosen ? theme.strings.shortenChooseLength : (usable.length ? '' : theme.strings.shortenNotPossible);
      if (!locked && shortenSelect.selectedOptions[0]?.disabled) shortenSelect.value = usable[usable.length - 1].value;
      if (!locked && dims && dims.OL) {
        const cut = Number(shortenSelect.selectedOptions[0].dataset.in);
        shortenResult.textContent = tr(theme.strings.shortenResult, { size: both(Math.round((dims.OL - cut) * 4) / 4) });
      } else {
        shortenResult.textContent = '';
      }
    }

    function update() {
      syncShorten();
      const addons = selectedAddons();
      const extra = addons.reduce((sum, a) => sum + a.price, 0);
      const total = variant.price + extra;

      priceEl.textContent = formatMoney(total);
      if (stickyPrice) stickyPrice.textContent = formatMoney(total);
      compareEl.hidden = !(variant.compare > variant.price);
      if (!compareEl.hidden) compareEl.textContent = formatMoney(variant.compare + extra);
      noteEl.hidden = addons.length === 0;
      noteEl.textContent = addons.length ? tr(theme.strings.addonsIncluded, { amount: formatMoney(extra) }) : '';

      // Option value labels next to each legend
      configSets.forEach((set) => {
        const c = checked(set);
        $('[data-option-value]', set).textContent = c ? dec(c.value) : '';
        if (c) set.classList.remove('is-invalid');
      });

      // Drawing
      if (draw) {
        if (lengthSet && widthSet) {
          const l = checked(lengthSet) || middle(lengthSet);
          const w = checked(widthSet) || middle(widthSet);
          const s = checked(shadeSet);
          draw({
            OL: dims.OL, OD: dims.OD, L: inchOf(l), W: inchOf(w),
            hex: s ? s.dataset.hex : null,
            lenLabel: checked(lengthSet) ? null : (theme.strings.chooseLength || 'choose a length'),
            widLabel: checked(widthSet) ? null : (theme.strings.chooseWidth || 'choose a width'),
          });
        } else if (dims.L) {
          draw({ OL: dims.OL, OD: dims.OD, L: dims.L, W: dims.W });
        }
      }

      // Work order
      if (workOrder) {
        const rows = [[theme.strings.model || 'Model', productTitle.split(' (')[0]]];
        configSets.forEach((set) => {
          const c = checked(set);
          rows.push([set.dataset.label || set.dataset.configOption, c ? dec(c.value) : '–']);
        });
        addons.forEach((a) => rows.push([`+ ${a.title}`, formatMoney(a.price)]));
        const html = rows.map(([k, v]) => `<div><dt>${k}</dt><dd>${v}</dd></div>`).join('') +
          `<div class="work-order__total"><dt>${theme.strings.total || 'Total'}</dt><dd data-total>${formatMoney(total)}</dd></div>`;
        const before = workOrder.innerHTML;
        workOrder.innerHTML = html;
        if (before && !reduceMotion) {
          const dd = $('[data-total]', workOrder);
          dd.classList.add('flash');
        }
      }

      // Buy it now would skip the add-ons, so there is none — the button always adds
      addBtn.disabled = !variant.available;
      addBtn.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
    }

    // Variant picker (add-on products with real variants)
    $$('[data-variant-option]', form).forEach((set) => {
      set.addEventListener('change', () => {
        const chosen = $$('[data-variant-option]', form).map((s) => $('input:checked', s).value);
        variant = variants.find((v) => v.options.every((o, i) => o === chosen[i])) || variant;
        idInput.value = variant.id;
        const picked = $('input:checked', set).value;
        $('[data-option-value]', set).textContent = /^[\d.]+"$/.test(picked) ? both(inches(picked)) : picked;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState(null, '', url);
        update();
      });
    });

    // Add-on checks: exclusive ones untick each other, extra fields open when ticked
    addonRows.forEach((row) => {
      const check = $('[data-addon-check]', row);
      const fields = $('.addon__fields', row);
      check.addEventListener('change', () => {
        if (check.checked && check.hasAttribute('data-exclusive')) {
          addonRows.forEach((other) => {
            const oc = $('[data-addon-check]', other);
            if (other !== row && oc.checked && oc.hasAttribute('data-exclusive')) {
              oc.checked = false;
              const of = $('.addon__fields', other);
              if (of) of.hidden = true;
            }
          });
        }
        if (fields) fields.hidden = !check.checked;
      });
    });

    form.addEventListener('change', update);

    // Submit: validate the sleeve's own options, then add sleeve + add-ons in one request
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      errorEl.hidden = true;
      const missing = configSets.filter((set) => !checked(set));
      if (missing.length) {
        missing.forEach((set) => set.classList.add('is-invalid'));
        errorEl.textContent = tr(theme.strings.pleaseChoose, { options: missing.map((s) => (s.dataset.label || s.dataset.configOption).toLowerCase()).join(', ') });
        errorEl.hidden = false;
        missing[0].scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        return;
      }

      const data = new FormData(form);
      const quantity = Number(data.get('quantity')) || 1;
      const properties = {};
      for (const [key, value] of data) {
        const m = key.match(/^properties\[(.+)\]$/);
        if (m && value !== '') properties[m[1]] = value;
      }
      const addons = selectedAddons();
      if (addons.length) properties._group = Date.now().toString(36);
      const items = [
        { id: variant.id, quantity, properties },
        ...addons.map((a) => ({ id: a.id, quantity, properties: { ...a.properties, _group: properties._group } })),
      ];

      addBtn.classList.add('is-loading');
      try {
        await cartPost(theme.routes.cartAdd, { items });
        if (cartDrawer()) {
          await refreshCart();
          openDrawer(cartDrawer());
        } else {
          window.location = theme.routes.cart;
        }
      } catch (err) {
        errorEl.textContent = err.message || theme.strings.addError;
        errorEl.hidden = false;
      } finally {
        addBtn.classList.remove('is-loading');
      }
    });

    // Mobile: sticky add bar once the main button scrolls out of view
    const sticky = $('[data-sticky-buy]', section);
    if (sticky && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-shown', show);
        sticky.setAttribute('aria-hidden', String(!show));
        $('button', sticky).tabIndex = show ? 0 : -1;
      }).observe(addBtn);
      $('[data-sticky-add]', sticky).addEventListener('click', () => form.requestSubmit());
    }

    // Mobile gallery dots
    const gallery = $('[data-gallery]', section);
    const dots = $$('.gallery__dots span', section);
    if (gallery && dots.length) {
      gallery.addEventListener('scroll', () => {
        const i = Math.round(gallery.scrollLeft / gallery.clientWidth);
        dots.forEach((d, n) => d.classList.toggle('is-active', n === i));
      }, { passive: true });
    }

    // Photo viewer: click a photo to enlarge, then flip with arrows, keys or swipe
    const viewer = $('[data-lightbox]', section);
    if (viewer && viewer.showModal) {
      const track = $('[data-lightbox-track]', viewer);
      const slides = $$('.lightbox__slide', viewer);
      const counter = $('[data-lightbox-count]', viewer);
      // Keep the photo number in a variable, so quick repeated clicks don't read a half-finished scroll
      let index = 0;
      let settle;
      const current = () => index;
      const sync = () => { if (counter) counter.textContent = `${index + 1} / ${slides.length}`; };
      const go = (i, smooth = true) => {
        index = (i + slides.length) % slides.length;
        track.scrollTo({ left: index * track.clientWidth, behavior: smooth ? 'smooth' : 'instant' });
        sync();
      };
      // After a swipe, read where the track came to rest
      track.addEventListener('scroll', () => {
        clearTimeout(settle);
        settle = setTimeout(() => { index = Math.round(track.scrollLeft / track.clientWidth); sync(); }, 150);
      }, { passive: true });
      $$('[data-zoom]', section).forEach((btn) => btn.addEventListener('click', () => {
        viewer.showModal();
        document.documentElement.classList.add('has-lightbox');
        go(Number(btn.dataset.zoom), false);
        sync();
      }));
      $('[data-lightbox-prev]', viewer)?.addEventListener('click', () => go(current() - 1));
      $('[data-lightbox-next]', viewer)?.addEventListener('click', () => go(current() + 1));
      $('[data-lightbox-close]', viewer).addEventListener('click', () => viewer.close());
      viewer.addEventListener('close', () => document.documentElement.classList.remove('has-lightbox'));
      viewer.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(current() - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); go(current() + 1); }
        if (e.key === 'Escape') { e.preventDefault(); viewer.close(); }
      });
      // A click on the dark backdrop (not the photo or a button) closes it
      viewer.addEventListener('click', (e) => { if (e.target === viewer || e.target.classList.contains('lightbox__slide')) viewer.close(); });
    }

    update();
  }

  // ─── Estimated ship date ──────────────────────────────────
  // Today (or the day after a holiday closure) plus N business days, weekends skipped.
  $$('[data-ship-estimate]').forEach((node) => {
    const days = Number(node.dataset.shipEstimate);
    if (!days) return;
    const date = new Date();
    date.setHours(12, 0, 0, 0);
    const closed = /^\d{4}-\d{2}-\d{2}$/.test(theme.closedUntil || '') ? new Date(`${theme.closedUntil}T12:00:00`) : null;
    if (closed && closed > date) date.setTime(closed.getTime());
    for (let added = 0; added < days;) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) added++;
    }
    const lang = document.documentElement.lang || 'en';
    const label = date.toLocaleDateString(lang === 'en' ? 'en-GB' : lang, { weekday: 'short', day: 'numeric', month: 'short' }); // EU order: Wed 28 Oct
    const template = node.hasAttribute('data-ready') ? theme.strings.shipsByReady : theme.strings.shipsBy;
    if (template) node.textContent = template.replace('{{ date }}', label);
  });

  // ─── Welcome offer ────────────────────────────────────────
  // Appears once after a delay or half a page of scroll; closing hides it for 14 days, signing up for good.
  const welcome = $('[data-welcome]');
  if (welcome) initWelcome(welcome);

  function initWelcome(box) {
    const KEY = 'lf-welcome';
    const store = {
      get() { try { return localStorage.getItem(KEY) || ''; } catch { return ''; } },
      set(v) { try { localStorage.setItem(KEY, v); } catch { /* private mode: it just shows again next visit */ } },
    };
    const state = store.get();
    const closedAt = state.startsWith('closed:') ? Number(state.slice(7)) : 0;
    // Set by Liquid after Shopify reloads the page from the sign-up: success shows the code, an error shows the form again
    const posted = box.hasAttribute('data-posted');
    const failed = box.hasAttribute('data-error');
    if (!posted && !failed && (state === 'done' || (closedAt && Date.now() - closedAt < 14 * 864e5))) return;

    const show = (step) => {
      if (step === 'done') showDone();
      if (!box.hidden) return;
      box.hidden = false;
      requestAnimationFrame(() => requestAnimationFrame(() => box.classList.add('is-open')));
    };
    const hide = () => {
      box.classList.add('is-closing');
      box.classList.remove('is-open');
      setTimeout(() => { box.hidden = true; box.classList.remove('is-closing'); }, reduceMotion ? 0 : 220);
    };
    const showDone = () => {
      $('[data-welcome-step="form"]', box).hidden = true;
      $('[data-welcome-step="done"]', box).hidden = false;
      store.set('done');
    };

    // Back from the sign-up: open straight away on the code (or on the form with Shopify's error message)
    if (posted) { show('done'); }
    else if (failed) { show(); }

    let timer = setTimeout(() => show(), (Number(box.dataset.delay) || 15) * 1000);
    const onScroll = () => {
      if (window.scrollY > (document.documentElement.scrollHeight - innerHeight) * 0.5) {
        clearTimeout(timer);
        window.removeEventListener('scroll', onScroll);
        show();
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    $('[data-welcome-close]', box).addEventListener('click', () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      if (store.get() !== 'done') store.set(`closed:${Date.now()}`);
      hide();
    });
    box.addEventListener('keydown', (e) => { if (e.key === 'Escape') $('[data-welcome-close]', box).click(); });

    // The form posts normally (not in the background) so Shopify's CAPTCHA can run; we only check the email first
    const form = $('#welcome-form', box);
    const error = $('[data-welcome-error]', box);
    if (form) form.addEventListener('submit', (e) => {
      const input = $('input[type="email"]', form);
      if (!input.checkValidity()) {
        e.preventDefault();
        error.textContent = theme.strings.invalidEmail;
        error.hidden = false;
        input.focus();
        return;
      }
      error.hidden = true;
      store.set('pending');
      $('button[type="submit"]', form).classList.add('is-loading');
    });

    $('[data-welcome-copy]', box).addEventListener('click', async () => {
      const hint = $('[data-welcome-copied]', box);
      try {
        await navigator.clipboard.writeText(box.dataset.code);
        hint.textContent = theme.strings.copied;
      } catch {
        hint.textContent = theme.strings.selectCopy;
      }
    });
  }

  // ─── Collection sort ──────────────────────────────────────
  const sort = $('[data-sort]');
  if (sort) sort.addEventListener('change', () => {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sort.value);
    url.searchParams.delete('page');
    window.location = url;
  });

  // ─── Headline split + reveals ─────────────────────────────
  $$('[data-split]').forEach((h) => {
    const text = h.textContent.trim();
    if (!text) return;
    h.setAttribute('aria-label', text);
    h.innerHTML = text.split(/\s+/)
      .map((w, i) => `<span class="split-word" aria-hidden="true"><span style="--i:${i}">${w}</span></span>`)
      .join(' ');
    requestAnimationFrame(() => requestAnimationFrame(() => h.classList.add('is-split')));
  });

  const targets = $$('[data-reveal], .row, svg.sleeve, .density li');
  const show = (t) => t.classList.add(t.matches('svg') ? 'is-poured' : 'is-in');
  if (!('IntersectionObserver' in window) || window.Shopify && window.Shopify.designMode) {
    targets.forEach(show);
  } else {
    const io = new IntersectionObserver((entries) => {
      let n = 0;
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const t = entry.target;
        if (t.hasAttribute('data-reveal')) t.style.setProperty('--delay', `${Math.min(n++, 6) * 60}ms`); // short stagger, capped
        show(t);
        io.unobserve(t);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.1 });
    targets.forEach((t) => io.observe(t));
  }
})();
